import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import notification from 'antd/lib/notification';

import { setSettings, switchSettingsModalVisible } from 'actions/settings-actions';
import { shortcutsActions } from 'actions/shortcuts-actions';
import SettingsModal from 'components/header/settings-modal/settings-modal';
import { CombinedState } from 'reducers';
import { CLIENT_SETTINGS_KEY, mergeClientSettings } from 'utils/client-settings';
import GlobalHotKeys from 'utils/mousetrap-react';

// Lives at the app root, not in Header, so saved settings, the dialog and F1/F2 work on every route.
export default function AppSettings(): JSX.Element {
    const dispatch = useDispatch();
    const keyMap = useSelector((state: CombinedState) => state.shortcuts.keyMap);
    const settingsVisible = useSelector((state: CombinedState) => state.settings.showDialog);
    const shortcutsVisible = useSelector((state: CombinedState) => state.shortcuts.visibleShortcutsHelp);
    const player = useSelector((state: CombinedState) => state.settings.player);
    const workspace = useSelector((state: CombinedState) => state.settings.workspace);

    useEffect(() => {
        try {
            const merged = mergeClientSettings({ player, workspace }, localStorage.getItem(CLIENT_SETTINGS_KEY));
            if (merged) dispatch(setSettings(merged));
        } catch {
            notification.error({
                message: 'Failed to load settings from local storage',
                className: 'cvat-notification-notice-load-settings-fail',
            });
        }
    }, []);

    const closeSettings = useCallback(() => dispatch(switchSettingsModalVisible(false)), []);

    const subKeyMap = {
        SWITCH_SHORTCUTS: keyMap.SWITCH_SHORTCUTS,
        SWITCH_SETTINGS: keyMap.SWITCH_SETTINGS,
    };

    const handlers = {
        SWITCH_SHORTCUTS: (event: KeyboardEvent) => {
            if (event) event.preventDefault();
            if (!settingsVisible) dispatch(shortcutsActions.switchShortcutsModalVisible(!shortcutsVisible));
        },
        SWITCH_SETTINGS: (event: KeyboardEvent) => {
            if (event) event.preventDefault();
            if (!shortcutsVisible) dispatch(switchSettingsModalVisible(!settingsVisible));
        },
    };

    return (
        <>
            <GlobalHotKeys keyMap={subKeyMap} handlers={handlers} />
            <SettingsModal visible={settingsVisible} onClose={closeSettings} />
        </>
    );
}
